import { Box, Button, Chip, CircularProgress, Grid, Stack } from '@mui/material';
import { useFormik } from 'formik';
import TextInput from '../components/TextInput';
import { FIELD_SIZE, Section, StickyActions } from '../components/Section';
import {
  experienceInitialValues,
  ExperienceValidationSchema,
  IExperience,
  JobTypeOptions
} from './Schema';
import {
  useCreateExperienceMutation,
  useCurrentUserDetailsQuery,
  useUpdateExperienceDetailMutation
} from 'redux/reducers/user/dashboard/tms-user';
import { useFetchExperienceDetailQuery } from 'redux/reducers/user/dashboard/tms-user';
import SelectInput from '../components/Select';
import { Cell } from 'react-table';
import { TbEdit } from 'react-icons/tb';
import React, { useState } from 'react';
import { useGetDistrictsQuery } from 'redux/reducers/user/dashboard/tms-location';
import { toast } from 'react-toastify';
import DataTable from 'components/ui/DataTable/data-table';

function TrainingExp({
  activeStep,
  setActiveStep
}: {
  activeStep: number;
  setActiveStep: (step: number) => void;
}) {
  const { data: CurrentUser, isLoading: isCurrentUserLoading } = useCurrentUserDetailsQuery();

  const TraineeId = CurrentUser?.id;
  const [experienceId, setExperienceId] = useState<string>('');

  const {
    mutateAsync: updateExperience,
    isLoading: isUpdating,
    isSuccess
  } = useUpdateExperienceDetailMutation(TraineeId ?? '', experienceId ?? '');

  const { mutateAsync: createExperience, isLoading: isCreating } = useCreateExperienceMutation(
    TraineeId ?? ''
  );

  const { data: ExperienceDetail, isLoading: isExperienceDetailLoading } =
    useFetchExperienceDetailQuery(TraineeId ?? '');

  const { data: Districts } = useGetDistrictsQuery('');

  const DistrictOptions =
    Districts?.map((district) => ({
      label: district.name_en,
      value: district.id.toString()
    })) ?? [];
  const DataForEdit: IExperience[] = ExperienceDetail?.data?.filter(
    (exp: any) => exp.id == experienceId
  );

  React.useEffect(() => {
    if (experienceId) {
      formik.resetForm({
        values: {
          work_exp_id: DataForEdit[0]?.work_exp_id?.toString() ?? '',
          from_date: DataForEdit[0]?.from_date ?? '',
          to_date: DataForEdit[0]?.to_date ?? '',
          organization: DataForEdit[0]?.organization ?? '',
          designation: DataForEdit[0]?.designation ?? '',
          department: DataForEdit[0]?.department ?? '',
          district: DataForEdit[0]?.district?.toString() ?? '',
          job_type: DataForEdit[0]?.job_type ?? ''
        }
      });
    }
  }, [experienceId]);

  const cols = [
    {
      Header: 'Organization',
      accessor: 'organization'
    },
    { Header: 'Department', accessor: 'department' },
    { Header: 'Designation', accessor: 'designation' },
    { Header: 'From Date', accessor: 'from_date' },
    { Header: 'To Date', accessor: 'to_date' },
    {
      Header: 'Status',
      Cell: (cell: Cell<any>) => (
        <TbEdit
          fontSize={22}
          style={{ cursor: 'pointer' }}
          onClick={(_) => {
            setExperienceId(cell?.row?.original?.id);
          }}
        />
      )
    }
  ];

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: ExperienceValidationSchema,
    initialValues: experienceInitialValues,

    onSubmit: (values) => {
      let executeFunc = createExperience;
      if (experienceId) {
        executeFunc = updateExperience;
      }

      if (experienceId) {
        formik.resetForm({ values: experienceInitialValues });
        executeFunc({ ...values, work_exp_id: experienceId })
          .then(() => {
            setExperienceId('');
          })
          .catch((err) => {
            toast.error(err?.response?.data[0]);
          });
      } else {
        const { work_exp_id, ...sendValue } = values;
        executeFunc(sendValue)
          .then(() => {
            formik.resetForm();
          })
          .catch((err) => {
            toast.error(err?.response?.data[0]);
          });
      }
    }
  });

  const isEditing = !!experienceId;
  const isSaving = isUpdating || isCreating || formik.isSubmitting;

  if (isCurrentUserLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Section
        title={isEditing ? 'Edit Experience' : 'Add Experience'}
        description="Record each role you have held, one entry at a time."
        action={isEditing && <Chip label="Editing entry" color="primary" size="small" />}
      >
        <SelectInput
          {...FIELD_SIZE}
          label="Job Type"
          name="job_type"
          options={JobTypeOptions || []}
          value={formik.values.job_type}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          required
        />
        <TextInput
          {...FIELD_SIZE}
          label="From Date"
          name="from_date"
          type="date"
          onChange={formik.handleChange}
          value={formik.values.from_date}
          formik={formik}
        />
        <TextInput
          {...FIELD_SIZE}
          label="To Date"
          name="to_date"
          type="date"
          onChange={formik.handleChange}
          value={formik.values.to_date}
          formik={formik}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Organization"
          name="organization"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.organization}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        <TextInput
          {...FIELD_SIZE}
          label="Designation"
          name="designation"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.designation}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        <TextInput
          {...FIELD_SIZE}
          label="Department"
          name="department"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.department}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        <SelectInput
          {...FIELD_SIZE}
          label="District"
          name="district"
          options={DistrictOptions || []}
          value={formik.values.district}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          required
        />

        <Grid item xs={12}>
          <Stack direction="row" justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : undefined}
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 120 }}
            >
              {isEditing ? 'Update' : 'Add'}
            </Button>
          </Stack>
        </Grid>
      </Section>

      <Section
        title="Experience History"
        description="Entries you have already saved. Use the edit icon to load one back into the form."
      >
        <Grid item xs={12}>
          <Box sx={{ overflowX: 'auto' }}>
            <DataTable
              data={ExperienceDetail?.data || []}
              columns={cols}
              isLoading={isExperienceDetailLoading}
              hidePagination={true}


            />
          </Box>
        </Grid>
      </Section>

      <StickyActions>
        {activeStep > 1 && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setActiveStep(--activeStep)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Previous Step
          </Button>
        )}

        <Button
          variant="outlined"
          onClick={() => {
            setActiveStep(++activeStep);
          }}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Next
        </Button>
      </StickyActions>
    </form>
  );
}

export default TrainingExp;
